// src/pages/offlineSales/OfflineSalesSettingsPage.jsx
import React, { useState, useEffect } from 'react';
import { Settings, Star, FileText, Save, UserCheck } from 'lucide-react';
import { PageHeader } from '../../components/common';
import MultiSelectFilter from '../../components/common/MultiSelectFilter';
import { getUserRoles } from '../../services/api/users';
import { useLocalization } from '../../contexts/LocalizationContext';
import { useAlert } from '../../contexts/AlertContext';
import { getSettings, updateSettings } from '../../services/api/offlineSales/settings';
import styles from './OfflineSalesSettingsPage.module.css';

const OfflineSalesSettingsPage = () => {
  const { t } = useLocalization();
  const { success, error } = useAlert();

  const [form, setForm] = useState({
    loyaltyEnabled: false,
    loyaltyPointsPerRupee: 1,
    loyaltyPointValueRupees: 0.1,
    loyaltyMinRedeemPoints: 100,
    loyaltyMaxRedeemPct: 50,
    receiptHeader: '',
    receiptFooter: '',
    salesRepRoleIds: [],
  });
  const [roles, setRoles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const load = async () => {
      try {
        const [result, rolesData] = await Promise.all([
          getSettings(),
          getUserRoles().catch(() => []),
        ]);
        setRoles(Array.isArray(rolesData) ? rolesData : (rolesData?.roles || []));
        const s = result.settings || result;
        if (s) {
          setForm({
            loyaltyEnabled: s.loyaltyEnabled || false,
            loyaltyPointsPerRupee: s.loyaltyPointsPerRupee ?? 1,
            loyaltyPointValueRupees: s.loyaltyPointValueRupees ?? 0.1,
            loyaltyMinRedeemPoints: s.loyaltyMinRedeemPoints ?? 100,
            loyaltyMaxRedeemPct: s.loyaltyMaxRedeemPct ?? 50,
            receiptHeader: s.receiptHeader || '',
            receiptFooter: s.receiptFooter || '',
            salesRepRoleIds: s.salesRepRoleIds || [],
          });
        }
      } catch (err) {
        error(err?.response?.data?.message || 'Failed to load settings');
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (field) => (e) => {
    const val = e.target.type === 'checkbox' ? e.target.checked
      : e.target.type === 'number' ? parseFloat(e.target.value) || 0
      : e.target.value;
    setForm(f => ({ ...f, [field]: val }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      setSaving(true);
      await updateSettings(form);
      success(t('offlineSales.saveSettings'));
    } catch (err) {
      error(err?.response?.data?.message || 'Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className={styles.loading}>
        <div className={styles.spinner} />
      </div>
    );
  }

  return (
    <div className={styles.page}>
      <PageHeader
        title={t('offlineSales.posSettings')}
        icon={Settings}
        description={t('offlineSales.posSettingsDesc', 'Configure loyalty programme and receipt layout for your POS shops.')}
      />

      <form onSubmit={handleSubmit} className={styles.form}>
        {/* ── Loyalty Programme ── */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <Star size={18} className={styles.sectionIcon} />
            <h2 className={styles.sectionTitle}>{t('offlineSales.loyaltySettings')}</h2>
          </div>

          <div className={styles.toggleRow}>
            <div className={styles.toggleInfo}>
              <span className={styles.fieldLabel}>{t('offlineSales.enableLoyalty')}</span>
              <span className={styles.fieldHint}>{t('offlineSales.enableLoyaltyHint', 'Customers earn points on purchases and can redeem them as discounts.')}</span>
            </div>
            <label className={styles.switch}>
              <input
                type="checkbox"
                checked={form.loyaltyEnabled}
                onChange={set('loyaltyEnabled')}
              />
              <span className={styles.slider} />
            </label>
          </div>

          {form.loyaltyEnabled && (
            <div className={styles.grid}>
              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{t('offlineSales.pointsPerRupee')}</label>
                <span className={styles.fieldHint}>{t('offlineSales.pointsPerRupeeHint', 'How many points earned per Rs. 1 spent')}</span>
                <input
                  type="number"
                  min="0.001"
                  step="0.001"
                  value={form.loyaltyPointsPerRupee}
                  onChange={set('loyaltyPointsPerRupee')}
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{t('offlineSales.pointValueRupees')}</label>
                <span className={styles.fieldHint}>{t('offlineSales.pointValueHint', 'Rupee value of 1 loyalty point when redeemed')}</span>
                <input
                  type="number"
                  min="0.001"
                  step="0.001"
                  value={form.loyaltyPointValueRupees}
                  onChange={set('loyaltyPointValueRupees')}
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{t('offlineSales.minRedeemPoints')}</label>
                <span className={styles.fieldHint}>{t('offlineSales.minRedeemHint', 'Minimum points required before a customer can redeem')}</span>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={form.loyaltyMinRedeemPoints}
                  onChange={set('loyaltyMinRedeemPoints')}
                  className={styles.input}
                />
              </div>

              <div className={styles.fieldGroup}>
                <label className={styles.fieldLabel}>{t('offlineSales.maxRedeemPct')}</label>
                <span className={styles.fieldHint}>{t('offlineSales.maxRedeemHint', 'Maximum % of order grand total that can be discounted via points')}</span>
                <div className={styles.inputWithUnit}>
                  <input
                    type="number"
                    min="1"
                    max="100"
                    step="1"
                    value={form.loyaltyMaxRedeemPct}
                    onChange={set('loyaltyMaxRedeemPct')}
                    className={styles.input}
                  />
                  <span className={styles.unit}>%</span>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* ── Receipt Settings ── */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <FileText size={18} className={styles.sectionIcon} />
            <h2 className={styles.sectionTitle}>{t('offlineSales.receiptSettings')}</h2>
          </div>

          <div className={styles.fieldGroup}>
            <label className={styles.fieldLabel}>{t('offlineSales.receiptHeader')}</label>
            <span className={styles.fieldHint}>{t('offlineSales.receiptHeaderPlaceholder')}</span>
            <textarea
              value={form.receiptHeader}
              onChange={set('receiptHeader')}
              rows={3}
              className={styles.textarea}
              placeholder={t('offlineSales.receiptHeaderPlaceholder')}
            />
          </div>

          <div className={styles.fieldGroup} style={{ marginTop: '1rem' }}>
            <label className={styles.fieldLabel}>{t('offlineSales.receiptFooter')}</label>
            <span className={styles.fieldHint}>{t('offlineSales.receiptFooterPlaceholder')}</span>
            <textarea
              value={form.receiptFooter}
              onChange={set('receiptFooter')}
              rows={3}
              className={styles.textarea}
              placeholder={t('offlineSales.receiptFooterPlaceholder')}
            />
          </div>
        </div>

        {/* ── Sales Representatives ── */}
        <div className={styles.section}>
          <div className={styles.sectionHeader}>
            <UserCheck size={18} className={styles.sectionIcon} />
            <h2 className={styles.sectionTitle}>{t('offlineSales.salesRepSettings')}</h2>
          </div>

          <div className={styles.fieldGroup}>
            <MultiSelectFilter
              label={t('offlineSales.salesRepRoles')}
              options={roles}
              selectedValues={form.salesRepRoleIds}
              onChange={(values) => setForm(f => ({ ...f, salesRepRoleIds: values }))}
              placeholder={t('offlineSales.salesRepRolesPlaceholder')}
              displayKey="name"
              valueKey="id"
              searchKey="name"
              floatingDropdown
            />
            <span className={styles.fieldHint}>{t('offlineSales.salesRepRolesHint')}</span>
          </div>
        </div>

        {/* ── Save ── */}
        <div className={styles.saveBar}>
          <button type="submit" disabled={saving} className={styles.saveBtn}>
            <Save size={16} />
            {saving ? t('common.saving', 'Saving...') : t('offlineSales.saveSettings')}
          </button>
        </div>
      </form>
    </div>
  );
};

export default OfflineSalesSettingsPage;
